/**
 * Showings for a listing (moved from ListingDetailPage.tsx): list / calendar toggle, schedule an
 * appointment, and update an appointment's outcome.
 */

import { useState, useRef, useEffect } from 'react';
import { formatTimestamp, asIsoDateTimeString } from '@resida/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { ShowingsCalendar } from '../../../components/leasing/ShowingsCalendar.js';
import {
  fetchShowingsForListing,
  scheduleAppointment,
  updateAppointment,
  type Lead,
  type Listing,
  type ShowingKind,
  type ShowingOutcome,
  type ShowingAppointment,
} from '../../../lib/leasing.api';
import { listingLeadsQueryOptions } from './PipelineBoard';

// ---------------------------------------------------------------------------
// Showing helpers
// ---------------------------------------------------------------------------

const KIND_LABELS: Record<ShowingKind, string> = {
  private: 'Private',
  open_house: 'Open House',
};

const KIND_COLORS: Record<ShowingKind, string> = {
  private: 'bg-info/15 text-info',
  open_house: 'bg-primary/15 text-primary',
};

const OUTCOME_LABELS: Record<ShowingOutcome, string> = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled',
};

const OUTCOME_COLORS: Record<ShowingOutcome, string> = {
  scheduled: 'bg-success/15 text-success',
  completed: 'bg-muted text-muted-foreground',
  cancelled: 'bg-destructive/15 text-destructive',
  rescheduled: 'bg-warning/15 text-warning',
};


function formatDateTimeShort(dateStr: string, timezone: string): string {
  return formatTimestamp(asIsoDateTimeString(dateStr), timezone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Schedule Appointment Modal (used on both listing and lead pages)
// ---------------------------------------------------------------------------

interface ScheduleAppointmentModalProps {
  listingId: string;
  prefilledLeadId?: string;
  prefilledDateTime?: string;
  defaultDurationMinutes?: number;
  leads?: Lead[];
  onClose: () => void;
  onSuccess: () => void;
}

function ScheduleAppointmentModal({
  listingId,
  prefilledLeadId,
  prefilledDateTime,
  defaultDurationMinutes,
  leads = [],
  onClose,
  onSuccess,
}: ScheduleAppointmentModalProps) {
  const [kind, setKind] = useState<ShowingKind>(prefilledLeadId ? 'private' : 'private');
  const [scheduledFor, setScheduledFor] = useState(prefilledDateTime ?? '');
  const [duration, setDuration] = useState(String(defaultDurationMinutes ?? 30));
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [leadId, setLeadId] = useState(prefilledLeadId ?? '');
  const [maxAttendees, setMaxAttendees] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape key + focus trap
  useEffect(() => {
    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Focus first focusable element on open
    const dialog = dialogRef.current;
    if (dialog) {
      const firstFocusable = dialog.querySelector<HTMLElement>(FOCUSABLE);
      firstFocusable?.focus();
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const inputCls =
    'w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!scheduledFor) { setError('Please select a date and time.'); return; }
    if (kind === 'private' && !leadId) { setError('Private showings require a lead.'); return; }
    setIsSubmitting(true);
    try {
      await scheduleAppointment(listingId, {
        kind,
        scheduledFor: new Date(scheduledFor).toISOString(),
        durationMinutes: duration ? parseInt(duration, 10) : undefined,
        location: location || null,
        notes: notes || null,
        leadId: kind === 'private' ? leadId : undefined,
        maxAttendees: kind === 'open_house' && maxAttendees ? parseInt(maxAttendees, 10) : null,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule appointment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="schedule-appointment-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-appointment-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={dialogRef} className="bg-card rounded-lg border border-border w-full max-w-md p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 id="schedule-appointment-title" className="text-lg font-semibold mb-4">
          Schedule Appointment
        </h2>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" data-testid="schedule-appointment-form">
          {/* Kind */}
          <fieldset>
            <legend className="block text-sm font-medium mb-1.5">Type</legend>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  data-testid="appointment-kind-private"
                  name="appointment-kind"
                  value="private"
                  checked={kind === 'private'}
                  onChange={() => setKind('private')}
                  disabled={!!prefilledLeadId}
                />
                Private (1:1)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  data-testid="appointment-kind-open-house"
                  name="appointment-kind"
                  value="open_house"
                  checked={kind === 'open_house'}
                  onChange={() => setKind('open_house')}
                  disabled={!!prefilledLeadId}
                />
                Open House
              </label>
            </div>
          </fieldset>

          {/* Date/Time */}
          <div>
            <label htmlFor="appointment-scheduled-for" className="block text-sm font-medium mb-1">
              Date &amp; Time <span className="text-destructive" aria-hidden="true">*</span>
            </label>
            <input
              id="appointment-scheduled-for"
              data-testid="appointment-scheduled-for"
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              required
              aria-required="true"
              className={inputCls}
              disabled={isSubmitting}
            />
          </div>

          {/* Duration */}
          <div>
            <label htmlFor="appointment-duration" className="block text-sm font-medium mb-1">
              Duration (minutes)
            </label>
            <input
              id="appointment-duration"
              data-testid="appointment-duration"
              type="number"
              min="5"
              step="5"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="30"
              className={inputCls}
              disabled={isSubmitting}
            />
          </div>

          {/* Location */}
          <div>
            <label htmlFor="appointment-location" className="block text-sm font-medium mb-1">
              Location <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              id="appointment-location"
              data-testid="appointment-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Front door, Unit 4B"
              className={inputCls}
              disabled={isSubmitting}
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="appointment-notes" className="block text-sm font-medium mb-1">
              Notes <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              id="appointment-notes"
              data-testid="appointment-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any notes for this appointment…"
              className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-y"
              disabled={isSubmitting}
            />
          </div>

          {/* Private: lead picker */}
          {kind === 'private' && (
            <div>
              <label htmlFor="appointment-lead" className="block text-sm font-medium mb-1">
                Lead <span className="text-destructive" aria-hidden="true">*</span>
              </label>
              {prefilledLeadId ? (
                <input
                  id="appointment-lead"
                  data-testid="appointment-lead-id"
                  type="text"
                  value={prefilledLeadId}
                  readOnly
                  aria-required="true"
                  className={`${inputCls} bg-muted cursor-not-allowed`}
                />
              ) : (
                <select
                  id="appointment-lead"
                  data-testid="appointment-lead-select"
                  aria-required="true"
                  value={leadId}
                  onChange={(e) => setLeadId(e.target.value)}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring"
                  disabled={isSubmitting}
                >
                  <option value="">— Select a lead —</option>
                  {leads.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.prospectName ?? l.prospectEmail}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Open house: max attendees */}
          {kind === 'open_house' && (
            <div>
              <label htmlFor="appointment-max-attendees" className="block text-sm font-medium mb-1">
                Max Attendees <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                id="appointment-max-attendees"
                data-testid="appointment-max-attendees"
                type="number"
                min="1"
                value={maxAttendees}
                onChange={(e) => setMaxAttendees(e.target.value)}
                placeholder="Leave blank for no cap"
                className={inputCls}
                disabled={isSubmitting}
              />
            </div>
          )}

          {error && (
            <div
              data-testid="schedule-appointment-error"
              className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <Button
              type="submit"
              data-testid="schedule-appointment-submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Scheduling…' : 'Schedule'}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="schedule-appointment-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CalendarOutcomeEditorModal — shown when an appointment is clicked in calendar view
// ---------------------------------------------------------------------------

interface CalendarOutcomeEditorModalProps {
  appt: ShowingAppointment;
  timezone: string;
  editingOutcome: ShowingOutcome;
  isUpdating: boolean;
  onOutcomeChange: (o: ShowingOutcome) => void;
  onSave: () => void;
  onCancel: () => void;
}

function CalendarOutcomeEditorModal({
  appt,
  timezone,
  editingOutcome,
  isUpdating,
  onOutcomeChange,
  onSave,
  onCancel,
}: CalendarOutcomeEditorModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const FOCUSABLE =
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onCancel(); return; }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    const dialog = dialogRef.current;
    if (dialog) {
      const firstFocusable = dialog.querySelector<HTMLElement>(FOCUSABLE);
      firstFocusable?.focus();
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="calendar-outcome-editor-title"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        ref={dialogRef}
        className="bg-card rounded-lg border border-border w-full max-w-sm p-5 shadow-lg"
      >
        <h2 id="calendar-outcome-editor-title" className="text-base font-semibold mb-1">
          Update Outcome
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          {formatDateTimeShort(appt.scheduledFor, timezone)} &middot; {appt.durationMinutes} min
        </p>

        <div
          className="flex items-center gap-2 flex-wrap"
          data-testid={`showing-outcome-editor-${appt.id}`}
        >
          <select
            data-testid={`showing-outcome-select-${appt.id}`}
            aria-label="Appointment outcome"
            value={editingOutcome}
            onChange={(e) => onOutcomeChange(e.target.value as ShowingOutcome)}
            className="border border-input rounded px-2 py-1 text-xs bg-background text-foreground min-h-[44px]"
            disabled={isUpdating}
          >
            {(['scheduled', 'completed', 'cancelled', 'rescheduled'] as ShowingOutcome[]).map((o) => (
              <option key={o} value={o}>{OUTCOME_LABELS[o]}</option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            className="min-h-[44px]"
            data-testid={`showing-outcome-save-${appt.id}`}
            disabled={isUpdating}
            onClick={onSave}
          >
            {isUpdating ? 'Saving…' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-[44px]"
            data-testid={`showing-outcome-cancel-${appt.id}`}
            onClick={onCancel}
            disabled={isUpdating}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Showings List (on listing detail page)
// ---------------------------------------------------------------------------

interface ShowingsSectionProps {
  listingId: string;
  isClosed: boolean;
  listing?: Listing | null;
}

export function ShowingsSection({ listingId, isClosed, listing }: ShowingsSectionProps) {
  const queryClient = useQueryClient();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingAppointmentId, setEditingAppointmentId] = useState<string | null>(null);
  const [editingOutcome, setEditingOutcome] = useState<ShowingOutcome>('scheduled');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [calendarView, setCalendarView] = useState(false);
  const [prefilledSlotDate, setPrefilledSlotDate] = useState<string | undefined>(undefined);

  const { data: showingsData, isLoading: showingsLoading, error: showingsError } = useQuery({
    queryKey: ['listing', listingId, 'showings'],
    queryFn: () => fetchShowingsForListing(listingId),
  });

  // For lead picker in schedule modal
  const { data: leadsData } = useQuery({
    ...listingLeadsQueryOptions(listingId),
    staleTime: 60_000,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['listing', listingId, 'showings'] });

  const handleUpdateOutcome = async (appointmentId: string) => {
    setIsUpdating(true);
    setUpdateError(null);
    try {
      await updateAppointment(listingId, appointmentId, { outcome: editingOutcome });
      setEditingAppointmentId(null);
      invalidate();
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update outcome');
    } finally {
      setIsUpdating(false);
    }
  };

  const appointments = showingsData?.items ?? [];
  const showingsTimezone = showingsData?.timezone ?? 'America/New_York';
  const leads = leadsData?.items ?? [];

  const handleAppointmentClick = (appointmentId: string) => {
    const appt = appointments.find((a) => a.id === appointmentId);
    if (appt) {
      setEditingAppointmentId(appointmentId);
      setEditingOutcome(appt.outcome);
      setUpdateError(null);
    }
  };

  const handleEmptySlotClick = (date: Date) => {
    // Format to datetime-local format (YYYY-MM-DDTHH:MM)
    const y = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    setPrefilledSlotDate(`${y}-${mo}-${d}T${h}:${mi}`);
    setShowScheduleModal(true);
  };

  return (
    <section data-testid="showings-section" className="mb-6">
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold mb-4">Showings</h2>
            {/* List / Calendar toggle */}
            <div className="flex rounded-md border border-border overflow-hidden">
              <button
                type="button"
                data-testid="showings-view-toggle-list"
                onClick={() => setCalendarView(false)}
                aria-label="List view"
                aria-pressed={!calendarView}
                className={[
                  'px-3 py-1 text-xs font-medium transition-colors min-h-[44px]',
                  !calendarView
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted',
                ].join(' ')}
              >
                List
              </button>
              <button
                type="button"
                data-testid="showings-view-toggle-calendar"
                onClick={() => setCalendarView(true)}
                aria-label="Calendar view"
                aria-pressed={calendarView}
                className={[
                  'px-3 py-1 text-xs font-medium transition-colors min-h-[44px]',
                  calendarView
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted',
                ].join(' ')}
              >
                Calendar
              </button>
            </div>
          </div>
          {!isClosed && (
            <Button
              type="button"
              size="sm"
              className="min-h-[44px]"
              data-testid="schedule-appointment-button"
              onClick={() => {
                setPrefilledSlotDate(undefined);
                setShowScheduleModal(true);
              }}
            >
              + Schedule Appointment
            </Button>
          )}
        </div>

        {showingsLoading && <Skeleton className="h-20 w-full" />}

        {showingsError && (
          <div
            data-testid="showings-error"
            className="p-3 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm"
            role="alert"
          >
            {showingsError instanceof Error ? showingsError.message : 'Failed to load showings'}
          </div>
        )}

        {updateError && (
          <div
            data-testid="showings-update-error"
            className="p-2 mb-2 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm"
            role="alert"
          >
            {updateError}
          </div>
        )}

        {/* Calendar view */}
        {calendarView && !showingsLoading && !showingsError && (
          <ShowingsCalendar
            appointments={appointments}
            onAppointmentClick={handleAppointmentClick}
            onEmptySlotClick={handleEmptySlotClick}
            timezone={showingsTimezone}
          />
        )}

        {/* Calendar-view outcome editor modal — shown when an appointment is clicked in calendar view */}
        {calendarView && editingAppointmentId && (() => {
          const appt = appointments.find((a) => a.id === editingAppointmentId);
          if (!appt) return null;
          return (
            <CalendarOutcomeEditorModal
              appt={appt}
              timezone={showingsTimezone}
              editingOutcome={editingOutcome}
              isUpdating={isUpdating}
              onOutcomeChange={(o) => setEditingOutcome(o)}
              onSave={() => void handleUpdateOutcome(appt.id)}
              onCancel={() => setEditingAppointmentId(null)}
            />
          );
        })()}

        {/* List view (default) */}
        {!calendarView && !showingsLoading && !showingsError && appointments.length === 0 && (
          <p className="text-sm text-muted-foreground italic">No showings scheduled yet.</p>
        )}

        {!calendarView && appointments.length > 0 && (
          <div className="space-y-2" data-testid="showings-list">
            {appointments.map((appt) => (
              <div
                key={appt.id}
                data-testid={`showing-row-${appt.id}`}
                className="rounded-md border border-border px-3 py-2.5 text-sm"
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-medium">{formatDateTimeShort(appt.scheduledFor, showingsTimezone)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {appt.durationMinutes} min
                      {appt.location && ` · ${appt.location}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {appt.attendees.length} attendee{appt.attendees.length !== 1 ? 's' : ''}
                      {appt.maxAttendees != null && ` / ${appt.maxAttendees} max`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                    <span
                      data-testid={`showing-kind-badge-${appt.id}`}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${KIND_COLORS[appt.kind]}`}
                    >
                      {KIND_LABELS[appt.kind]}
                    </span>
                    <span
                      data-testid={`showing-outcome-badge-${appt.id}`}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${OUTCOME_COLORS[appt.outcome]}`}
                    >
                      {OUTCOME_LABELS[appt.outcome]}
                    </span>
                    {!isClosed && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="min-h-[44px]"
                        data-testid={`showing-update-outcome-${appt.id}`}
                        onClick={() => {
                          setEditingAppointmentId(appt.id);
                          setEditingOutcome(appt.outcome);
                          setUpdateError(null);
                        }}
                      >
                        Update
                      </Button>
                    )}
                  </div>
                </div>

                {/* Inline outcome editor */}
                {editingAppointmentId === appt.id && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`showing-outcome-editor-${appt.id}`}>
                    <select
                      data-testid={`showing-outcome-select-${appt.id}`}
                      aria-label="Appointment outcome"
                      value={editingOutcome}
                      onChange={(e) => setEditingOutcome(e.target.value as ShowingOutcome)}
                      className="border border-input rounded px-2 py-1 text-xs bg-background text-foreground"
                      disabled={isUpdating}
                    >
                      {(['scheduled', 'completed', 'cancelled', 'rescheduled'] as ShowingOutcome[]).map((o) => (
                        <option key={o} value={o}>{OUTCOME_LABELS[o]}</option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      size="sm"
                      className="min-h-[44px]"
                      data-testid={`showing-outcome-save-${appt.id}`}
                      disabled={isUpdating}
                      onClick={() => void handleUpdateOutcome(appt.id)}
                    >
                      {isUpdating ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="min-h-[44px]"
                      data-testid={`showing-outcome-cancel-${appt.id}`}
                      onClick={() => setEditingAppointmentId(null)}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {showScheduleModal && (
          <ScheduleAppointmentModal
            listingId={listingId}
            leads={leads}
            prefilledDateTime={prefilledSlotDate}
            defaultDurationMinutes={listing?.defaultShowingDurationMinutes ?? 30}
            onClose={() => {
              setShowScheduleModal(false);
              setPrefilledSlotDate(undefined);
            }}
            onSuccess={invalidate}
          />
        )}
      </div>
    </section>
  );
}
